import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, ChevronDown, ChevronUp, Mail, Flame } from "lucide-react";

export default function AdminRemakeRequests() {
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});

  const load = () => api.get("/admin/remake-requests").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (pid) => setExpanded((e) => ({ ...e, [pid]: !e[pid] }));

  const del = async (id) => {
    if (!confirm("Remove this request?")) return;
    try {
      await api.delete(`/admin/remake-requests/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const totalRequests = groups.reduce((s, g) => s + g.count, 0);

  return (
    <div data-testid="admin-remakes-page">
      <div className="flex items-baseline gap-3 mb-2 flex-wrap">
        <h1 className="font-display text-3xl sm:text-4xl text-[#F5F5DC]">Remake Requests</h1>
        <span className="smallcaps text-[#A3A3A3]">
          {totalRequests} {totalRequests === 1 ? "request" : "requests"} across {groups.length} {groups.length === 1 ? "piece" : "pieces"}
        </span>
      </div>
      <p className="text-[#A3A3A3] text-sm mb-10">Sorted by popularity — most-requested at the top.</p>

      <div className="space-y-4">
        {groups.map((g, idx) => (
          <div key={g.product_id} className="bg-[#111] border border-[#2A2A2A]" data-testid={`remake-group-${g.product_slug}`}>
            <button
              onClick={() => toggle(g.product_id)}
              className="w-full text-left p-5 flex items-center gap-4 hover:bg-[#161616] transition-colors"
            >
              <div className="flex items-center justify-center w-12 h-12 bg-[#0A0A0A] border border-[#2A2A2A] flex-shrink-0">
                {idx === 0 && g.count > 1 ? (
                  <Flame size={18} className="text-[#B87333]" />
                ) : (
                  <span className="font-display text-xl text-[#B87333]">{g.count}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-xl text-[#F5F5DC] truncate">{g.product_name || "Unknown piece"}</p>
                <p className="text-xs text-[#A3A3A3] mt-0.5">
                  {g.count} {g.count === 1 ? "person" : "people"} waiting · last asked {new Date(g.requests[0].created_at).toLocaleDateString()}
                </p>
              </div>
              {g.product_slug && (
                <Link
                  to={`/product/${g.product_slug}`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-[#B87333] hidden sm:inline"
                >
                  View →
                </Link>
              )}
              {expanded[g.product_id] ? <ChevronUp size={18} className="text-[#A3A3A3]" /> : <ChevronDown size={18} className="text-[#A3A3A3]" />}
            </button>

            {expanded[g.product_id] && (
              <div className="border-t border-[#2A2A2A] divide-y divide-[#2A2A2A]" data-testid={`remake-list-${g.product_slug}`}>
                {g.requests.map((r) => (
                  <div key={r.id} className="p-5 flex gap-4" data-testid={`remake-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-[#F5F5DC] text-sm">{r.customer_name}</p>
                      <p className="text-xs text-[#A3A3A3] mt-1">
                        <a href={`mailto:${r.customer_email}?subject=${encodeURIComponent("Your remake request — " + (r.product_name || ""))}`} className="hover:text-[#B87333] inline-flex items-center gap-1">
                          <Mail size={11} /> {r.customer_email}
                        </a>
                      </p>
                      <p className="text-[10px] text-[#555] mt-1">{new Date(r.created_at).toLocaleString()}</p>
                      {r.notes && (
                        <p className="text-sm text-[#A3A3A3] mt-2 whitespace-pre-wrap border-l-2 border-[#2A2A2A] pl-3">{r.notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => del(r.id)}
                      className="text-[#A3A3A3] hover:text-[#CF6679] p-2 -mt-1 -mr-1 self-start"
                      data-testid={`remake-del-${r.id}`}
                      aria-label="Remove request"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {groups.length === 0 && (
          <p className="text-[#A3A3A3] py-12 text-center">No remake requests yet. They&apos;ll appear here when customers ask about sold-out pieces.</p>
        )}
      </div>
    </div>
  );
}
